import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { take } from 'rxjs';
import { LessonService } from '../api/mylb/services/lesson.service';
import { ILessonType } from '../api/mylb/interfaces/ilesson-type.interface';
import { ILessonAttendanceStatus } from '../api/mylb/interfaces/ilesson-attendance.interface';

export interface ILessonTypeFilter {
	enrollmentId?: number;
	programId?: number;
	learnerLevel?: string;
	instructorId?: number;
}

interface IProgramMetadataCache {
	lessonTypes: ILessonType[];
	attendanceStatuses: ILessonAttendanceStatus[];
	typesLoaded: boolean;
	statusesLoaded: boolean;
	typesLastLoaded: number;
	statusesLastLoaded: number;
	// Track ongoing requests to prevent duplicates
	typesLoading: boolean;
	statusesLoading: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class LessonMetadataService {
	private _lessonService = inject(LessonService);

	// Program-based cache using Map
	private _programMetadataCache = signal<Map<number, IProgramMetadataCache>>(new Map());
	private _loading = signal<boolean>(false);

	// Cache expiry (5 minutes)
	private _cacheExpiryTime = 5 * 60 * 1000;

	// Public getters
	public readonly loading = computed(() => this._loading());

	/**
	 * Get lesson types for a specific program with caching
	 */
	public getLessonTypesForProgram(programId: number): Signal<ILessonType[]> {
		return computed(() => {
			const cache = this._programMetadataCache().get(programId);
			return cache?.lessonTypes || [];
		});
	}

	/**
	 * Get attendance statuses for a specific program with caching
	 */
	public getAttendanceStatusesForProgram(programId: number): Signal<ILessonAttendanceStatus[]> {
		return computed(() => {
			const cache = this._programMetadataCache().get(programId);
			return cache?.attendanceStatuses || [];
		});
	}

	/**
	 * Get lesson type options formatted for dropdowns
	 */
	public getLessonTypeOptions(programId: number): Signal<{ value: number; label: string }[]> {
		return computed(() => {
			return this.getLessonTypesForProgram(programId)().map((type) => ({
				value: type.lessonTypeID,
				label: type.typeName
			}));
		});
	}

	/**
	 * Get attendance status options formatted for dropdowns
	 */
	public getAttendanceStatusOptions(programId: number): Signal<{ value: number; label: string }[]> {
		return computed(() => {
			return this.getAttendanceStatusesForProgram(programId)().map((status: ILessonAttendanceStatus) => ({
				value: status.lessonAttendanceStatusID,
				label: status.lessonAttendanceStatusName
			}));
		});
	}

	/**
	 * Initialize metadata for a specific program
	 */
	public initializeForProgram(programId: number): void {
		this.loadLessonTypesForProgram(programId);
		this.loadAttendanceStatusesForProgram(programId);
	}

	/**
	 * Force refresh of cached data for a specific program
	 */
	public refreshProgram(programId: number): void {
		const currentCache = this._programMetadataCache();
		const programCache = currentCache.get(programId);

		if (programCache) {
			programCache.typesLoaded = false;
			programCache.statusesLoaded = false;
			programCache.typesLoading = false;
			programCache.statusesLoading = false;
			currentCache.set(programId, programCache);
			this._programMetadataCache.set(new Map(currentCache));
		}

		this.initializeForProgram(programId);
	}

	/**
	 * Clear all cached data
	 */
	public clearCache(): void {
		this._programMetadataCache.set(new Map());
	}

	/**
	 * Check if types are loaded for a program
	 */
	public typesLoadedForProgram(programId: number): boolean {
		const cache = this._programMetadataCache().get(programId);
		return cache?.typesLoaded || false;
	}

	/**
	 * Check if statuses are loaded for a program
	 */
	public statusesLoadedForProgram(programId: number): boolean {
		const cache = this._programMetadataCache().get(programId);
		return cache?.statusesLoaded || false;
	}

	/**
	 * Check if both types and statuses are loaded for a program
	 */
	public isLoadedForProgram(programId: number): Signal<boolean> {
		return computed(() => {
			const cache = this._programMetadataCache().get(programId);
			return (cache?.typesLoaded && cache?.statusesLoaded) || false;
		});
	}

	/**
	 * Get or create cache entry for a program
	 */
	private getOrCreateProgramCache(programId: number): IProgramMetadataCache {
		const currentCache = this._programMetadataCache();
		let programCache = currentCache.get(programId);

		if (!programCache) {
			programCache = {
				lessonTypes: [],
				attendanceStatuses: [],
				typesLoaded: false,
				statusesLoaded: false,
				typesLastLoaded: 0,
				statusesLastLoaded: 0,
				typesLoading: false,
				statusesLoading: false
			};
			currentCache.set(programId, programCache);
			this._programMetadataCache.set(new Map(currentCache));
		}

		return programCache;
	}

	/**
	 * Update cache entry for a program
	 */
	private updateProgramCache(programId: number, updates: Partial<IProgramMetadataCache>): void {
		const currentCache = this._programMetadataCache();
		const programCache = this.getOrCreateProgramCache(programId);

		Object.assign(programCache, updates);
		currentCache.set(programId, programCache);
		this._programMetadataCache.set(new Map(currentCache));
	}

	private loadLessonTypesForProgram(programId: number): void {
		const now = Date.now();
		const programCache = this.getOrCreateProgramCache(programId);

		// Check cache expiry
		if (programCache.typesLoaded && now - programCache.typesLastLoaded < this._cacheExpiryTime) {
			return;
		}

		// Prevent duplicate requests - if already loading, don't start another
		if (programCache.typesLoading) {
			return;
		}

		// Mark as loading before starting the request
		this.updateProgramCache(programId, { typesLoading: true });
		this._loading.set(true);

		this._lessonService
			.getLessonTypesForProgram(programId)
			.pipe(take(1))
			.subscribe({
				next: (types) => {
					this.updateProgramCache(programId, {
						lessonTypes: types,
						typesLoaded: true,
						typesLastLoaded: now,
						typesLoading: false
					});
					this._loading.set(false);
				},
				error: (error) => {
					console.error(`Failed to load lesson types for program ${programId}:`, error);
					this.updateProgramCache(programId, { typesLoading: false });
					this._loading.set(false);
				}
			});
	}

	private loadAttendanceStatusesForProgram(programId: number): void {
		const now = Date.now();
		const programCache = this.getOrCreateProgramCache(programId);

		// Check cache expiry
		if (programCache.statusesLoaded && now - programCache.statusesLastLoaded < this._cacheExpiryTime) {
			return;
		}

		// Prevent duplicate requests - if already loading, don't start another
		if (programCache.statusesLoading) {
			return;
		}

		// Mark as loading before starting the request
		this.updateProgramCache(programId, { statusesLoading: true });
		this._loading.set(true);

		this._lessonService
			.getAttendanceStatusesForProgram(programId)
			.pipe(take(1))
			.subscribe({
				next: (statuses) => {
					this.updateProgramCache(programId, {
						attendanceStatuses: statuses,
						statusesLoaded: true,
						statusesLastLoaded: now,
						statusesLoading: false
					});
					this._loading.set(false);
				},
				error: (error) => {
					console.error(`Failed to load attendance statuses for program ${programId}:`, error);
					this.updateProgramCache(programId, { statusesLoading: false });
					this._loading.set(false);
				}
			});
	}
}
